import { SearchRequest } from '~/dtos/plan-search';
import { ProviderTypes } from '~/dtos/provider-consts';

export const defaultProviderLogo =
  'https://amethyst-sam-25.tiiny.site/warranty_provider.png.image.png?height=150&width=112';

export const PRICING_PAYMENTOPTION_INDEX_UPFRONT = 0;
export const PRICING_PAYMENTOPTION_INDEX_MONTHLY = 1;

export interface WarrantyPilotPlanListingResponse {
  plans: PlanInfo[][];
  searchRequest: SearchRequest;
}

export interface WarrantyPilotPlanDetailsResponse extends PlanDetailsResponse {
  searchRequest: SearchRequest;
  sendPaymentToCrm: boolean;
  provider: Provider;
}

export interface PlanDetailsResponse {
  selectedPlanId: number;
  userId: string;
  searchId: string;
  providerName: string;
  providerInfo: string;
  overallRating: number;
  ratings: Rating[];
  monthlyAmountOfSales: number;
  reviews: Review[];
  plans: Plan[];
  inclusions: Inclusion[];
  staticInclusions: StaticInclusion[];
  notCoveredParts: string;
}

export interface Provider {
  id: string;
  name: string;
  type: ProviderTypes;
  logo?: string;
  description?: string;
  link?: string;
  crmProvider?: string;
}

export interface PlanInfo {
  id: number;
  optionId: string;
  name: string;
  provider: Provider;
  durationMonths: number;
  durationMiles: number;
  isAdditionalOdometerPlan: boolean;
  price?: {
    monthly: number;
    upfront?: number;
  };
  financeTerms: number;
  rating?: number;
  coverage: string[][];
  recommended?: boolean;
  bestCoverage?: boolean;
  coverageLevel?: string;
}

export interface PlanDetailsRequest {
  searchRequest: SearchRequest;
  provider: Provider;
}

export interface UseStripe {
  useStripe: boolean;
  stripeUrl: string;
}

export interface Review {
  id: string;
  sourceName: string;
  rate: number;
  numberOfReviews: number;
  url: string;
}

export interface Rating {
  sourceName: string;
  url: string;
  rating: number;
  grade: string;
}

export interface ProviderRating {
  name: string;
  overallRating: number;
  ratings: Rating[];
}

export type Plan = $.CheckoutPlanDto['planInformation']['selectedPlan'];

export interface PlanOption {
  selectedPlanId: number;
  optionId: string;
  isAdditionalOdometerPlan?: boolean;
  mileage: number;
  duration: number;
  price: Price;
  contractUrl: string;
}

export interface Price {
  monthlyPrice: number;
  totalPrice: number;
  paymentOptions: PaymentOption[];
}

export interface PaymentOption {
  pricePerInstallment: number;
  discount: number;
  installments: number;
  downpayment: number;
  totalPrice: number;
}

export interface Service {
  id: number;
  name: string;
  description: string;
  planId: number;
}

export interface StaticInclusion {
  categoryTitle: string;
  text: string;
}

export interface Inclusion {
  categoryTitle: string;
  categoryId: number;
  totalInclusionsNumber: number;
  coveredInclusionsNumber: number;
  headlines: string[];
  list: InclusionItem[];
}

export interface InclusionItem {
  itemName: string;
  isAvailableInSelectedPlan: boolean;
  info: string;
  items: InclusionDetail[];
}

export interface InclusionDetail {
  type: number;
  isIncluded: boolean;
  value: string | null;
}

export interface VinVerificationRequest {
  vin: string;
  state: string;
  mileage: string;
  selectedPlanId: number;
  selectedPlanOptionId: number;
  provider: Provider;
}

export const WARRANTYPILOT_PRODUCT_NAMES = {
  EXCLUSIONARY: 'Exclusionary',
  PREMIUM: 'Premium',
  POWERTRAIN_PLUS: 'Powertrain Plus',
  POWERTRAIN: 'Powertrain',
} as const;

export type WarrantyPilotProductNames = ObjectValues<typeof WARRANTYPILOT_PRODUCT_NAMES>;

export const WARRANTYPILOT_COVERAGE_TYPES = {
  ULTIMATE: 'Ultimate',
  ADVANCED: 'Advanced',
  ESSENTIAL: 'Essential',
} as const;

export const WARRANTYPILOT_COVERAGE_TYPE_RANKS = {
  ULTIMATE: '1',
  ADVANCED: '2',
  ESSENTIAL: '3',
} as const;

export type WarrantyPilotCoverageTypes = ObjectValues<typeof WARRANTYPILOT_COVERAGE_TYPES>;

export const WARRANTYPILOT_INCLUSION_CATEGORIES = {
  ENGINE: 'Engine',
  TRANSFER_CASE: 'Transfer Case',
  TRANSMISSION: 'Transmission',
  DRIVE_AXLE: 'Drive Axle',
  ELECTRICAL: 'Electrical',
  SUSPENSION: 'Suspension',
  STEERING: 'Steering',
  BRAKES: 'Brakes',
  FUEL_SYSTEM: 'Fuel System',
  COOLING_SYSTEM: 'Cooling System',
  EXHAUST: 'Exhaust',
  DIFFERENTIAL_ASSEMBLY: 'Differential Assembly',
  ADVANCED_ELECTRICAL: 'Advanced Electrical',
  TURBO_SUPER_CHARGER: 'Turbo/Super Charger',
  AIR_CONDITION: 'Air Conditioning',
  SEALS_AND_GASKETS: 'Seals and Gaskets',
} as const;

export type WarrantyPilotInclusionCategories = ObjectValues<typeof WARRANTYPILOT_INCLUSION_CATEGORIES>;

export const WARRANTYPILOT_UNCOMMON_INCLUSION_CATEGORIES = {
  HIGHTECH_OPTIONS: 'High-Tech Options',
  ANTILOCK_BRAKES: 'Anti-Lock Brakes',
  HYBRID: 'Hybrid',
  DRIVE_SYSTEM: 'Drive System',
  AUDIO: 'Audio',
  INTERIOR_EXTERIOR: 'Interior and Exterior',
  ELECTRONICS: 'Electronics',
  SPORT_UTILITY: 'Sport Utility',
  FRONT_SUSPENSION: 'Front Suspension',
  ROTARY_ENGINE: 'Rotary Engine',
} as const;

export type WarrantyPilotUncommonInclusionCategories = ObjectValues<typeof WARRANTYPILOT_UNCOMMON_INCLUSION_CATEGORIES>;

export const RATINGS_SOURCES = {
  BBB: 'BBB',
  BBBCustomer: 'BBBCustomer',
  Google: 'Google',
  Trustpilot: 'Trustpilot',
  Facebook: 'Facebook',
} as const;

export type RatingsSources = ObjectValues<typeof RATINGS_SOURCES>;

export const INVALID_SEARCH_REASONS = {
  InvalidState: 'Invalid State',
} as const;

export type InvalidSearchReasons = ObjectValues<typeof INVALID_SEARCH_REASONS>;
